<template>
  <div>
    <my-header></my-header>

    <div class="content-container-level-1">
      <h2>Schedule river rumble</h2>

      <form @submit.prevent="createRecordWithConfirmation">

<!--        <div class="form-group">-->
<!--          <label for="quest">Quest (example: @river_rumble/01_water_lilies23)</label>-->
<!---->
<!--          <vue-bootstrap-typeahead-->
<!--              v-model="riverRumbleAlias"-->
<!--              :data="riverRumbleSuggestions"-->
<!--              :minMatchingChars="1"-->
<!--              required></vue-bootstrap-typeahead>-->
<!--        </div>-->

<!--        <hr>-->

        <div class="form-group">
          <label for="start-at">Start at:</label>
          <date-time-input id="start-at" v-model="startAt"></date-time-input>
        </div>

        <hr>

        <div class="form-group">
          <h5>Duration</h5>
          <duration-input v-model="duration"></duration-input>
          <p class="mb-3">
            End at<br>
            {{ endAt | getMomentStr }}
          </p>
        </div>

        <hr>

        <div class="form-group">
          <label for="seed">Seed</label>
          <div class="d-flex seed-container">
            <div class="d-flex flex-column flex-grow-1">
              <input v-model.number="seed" id="seed" class="form-control" type="number" min="0">
            </div>
            <div class="d-flex flex-column">
              <b-button variant="primary" @click="randomizeSeed">Randomize</b-button>
            </div>
          </div>
        </div>

        <hr>

        <div class="form-group">
          <label for="minLevel">Min level</label>
          <input v-model.number="minLevel" id="minLevel" class="form-control" type="number" min="0">
        </div>

        <hr>

        <div class="form-group">
          <h5>Min time until end for start</h5>
          <duration-input v-model="minTimeUntilEndForStart"></duration-input>
          <p class="mb-3">
            Start available until<br>
            {{ startAvailableUntil | getMomentStr }}
          </p>
        </div>

        <hr>

        <div class="form-group">
          <h5><label for="minClientVersion">Min client version</label></h5>
          <input v-model="minClientVersion" id="minClientVersion" class="form-control">
          <p class="form-text">
            Min value (forced version): {{ forcedVersion }}
          </p>
          <p class="form-text">
            Max value (server version): {{ serverVersion }}
          </p>
        </div>

        <hr>

        <div class="form-group">
          <label for="playerGroupId">Segment:</label>
          <b-form-select
              id="playerGroupId"
              v-model="playerGroupId"
              :options="playerGroupIdOptions"
          />
        </div>

        <hr>

        <div class="form-group">
          <b-form-checkbox v-model="isRepeatRequired">Repeat</b-form-checkbox>
        </div>

        <fieldset class="form-group" v-if="isRepeatRequired">
          <legend>Repetition periods</legend>
          <div class="form-group">
            <label for="pause-between-river-rumbles">Pause between river rumbles (hour):</label>
            <b-form-input id="pause-between-river-rumbles" v-model="pauseBetweenRiverRumbles" type="number" number
                          required></b-form-input>
          </div>
          <div class="form-group">
            <label for="repeat-until">Repeat until:</label>
            <date-time-input id="repeat-until" v-model="repeatUntil"></date-time-input>
          </div>
          <periods-table :periods="repeatPeriods"></periods-table>
        </fieldset>

        <b-button :to="{name: 'river_rumble_timetable'}">Cancel</b-button>
        <b-button type="submit" variant="primary">Create</b-button>
      </form>
    </div>

    <loading-indicator v-if="isLoading"/>
  </div>
</template>

<script>
import {confirmAction} from '../../service/confirm';

import {convertVersionCodeToStr, getMomentStr, getRepeatedPeriods, Period,} from '../../util';

import {DEFAULT_RIVER_RUMBLE_PROTO_ID, Fnv32a, PeriodWithSeed, Random} from '../../model/river_rumble';

import notifier from '../../notifier';
import {HTTP} from '../../http-common';

import {DEFAULT_PLAYER_GROUP_ID, getPlayerGroupOptionsVerbose} from '../../model/event.js';

import MyHeader from '../Header.vue';
import LoadingIndicator from '../LoadingIndicator.vue';
import DateTimeInput from '../DateTimeInput.vue';
import DurationInput from '../DurationInput.vue';
import PeriodsTable from './PeriodsTable.vue';

export default {
  name: 'AddRiverRumbleTimetableRecord',

  components: {
    MyHeader,
    LoadingIndicator,
    DateTimeInput,
    DurationInput,
    PeriodsTable,
  },

  data() {
    let now = Date.now();
    now -= (now % 60000);

    return {
      isLoading: false,

      // seedRandomizer sequence is dependent on initStamp;
      // repeat creation depends on hash(initStamp, seed)
      initStamp: 0,
      seedRandomizer: null,

      riverRumbleAlias: '',
      riverRumbleSuggestions: [],

      playerGroupIdOptions: getPlayerGroupOptionsVerbose(),

      startAt: now,
      duration: 0,
      seed: 0,
      minLevel: 0,
      minTimeUntilEndForStart: 0,

      forcedVersion: '',
      serverVersion: '',
      minClientVersion: '',
      playerGroupId: DEFAULT_PLAYER_GROUP_ID,

      isRepeatRequired: false,
      pauseBetweenRiverRumbles: 24,
      repeatUntil: now,
    };
  },

  computed: {
    endAt() {
      return this.startAt / 1000 + this.duration;
    },

    startAvailableUntil() {
      return this.endAt - this.minTimeUntilEndForStart;
    },

    repeatPeriods() {
      const startPeriod = new Period(this.startAt / 1000, this.endAt);
      const periods = getRepeatedPeriods(startPeriod, this.pauseBetweenRiverRumbles * 3600, this.repeatUntil / 1000);

      const randSeed = new Fnv32a();
      randSeed.writeUint32(this.initStamp);
      randSeed.writeUint32(this.seed);

      const seedRandomizer = new Random(randSeed.value);

      const periodsWithSeeds = [];
      for (const period of periods) {
        if (periodsWithSeeds.length === 0) {
          // first item clones input seed, next seeds generate based on its value
          periodsWithSeeds.push(new PeriodWithSeed(period, this.seed));
        } else {
          periodsWithSeeds.push(new PeriodWithSeed(period, seedRandomizer.next() % 10000));
        }
      }
      return periodsWithSeeds;
    },
  },

  created() {
    this.initStamp = Date.now() / 1000;
    this.initSeedRandomizer();
    this.loadLastRecord();
    this.randomizeSeed();
  },

  watch: {
    riverRumbleAlias(newRiverRumbleAlias) {
      HTTP.get(
          '/river_rumble/configs',
          {
            params: {
              q: newRiverRumbleAlias
            }
          }
      )
          .then(response => {
            const aliases = [];
            for (var i = 0; i < response.data.data.length; ++i) {
              const alias = response.data.data[i];
              aliases.push(alias);
            }
            this.riverRumbleSuggestions = aliases;
          })
          .catch(e => {
            notifier.showError(e);
          })
    },
  },

  methods: {
    loadLastRecord() {
      notifier.deleteSenderNotifications(this);

      this.isLoading = true;

      const url = '/river_rumble/timetable/adding_info';
      HTTP.get(url)
          .then(response => {
            const data = response.data.data;
            if (data === null) {
              notifier.showError('Invalid server response. data === null.', {sender: this});
              return;
            }

            const lastRecord = data.last_record;
            if (lastRecord.id > 0) {
              // const endTime = (lastRecord.stime + lastRecord.duration) * 1000;
              // this.startAt = endTime;
              this.minLevel = lastRecord.min_level;
              this.duration = lastRecord.duration;
              this.minTimeUntilEndForStart = lastRecord.min_time_until_end_for_start;
              // this.repeatUntil = endTime;
            }

            this.forcedVersion = convertVersionCodeToStr(data.forced_version);
            this.serverVersion = convertVersionCodeToStr(data.server_version);
          })
          .catch(e => {
            notifier.showError(e, {sender: this});
          })
          .finally(() => {
            this.isLoading = false;
          });
    },

    createRecordWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const record = this.makeRecord();

      this.confirmRecordCreation(() => {
        this.createRecord(record);
      });
    },

    makeRecord() {
      return {
        protoId: DEFAULT_RIVER_RUMBLE_PROTO_ID, // getProtoId(this.riverRumbleAlias),
        startAt: this.startAt / 1000,
        duration: this.duration,
        seed: this.seed,
        minLevel: this.minLevel,
        minTimeUntilEndForStart: this.minTimeUntilEndForStart,
        minClientVersion: this.minClientVersion,
        playerGroupId: this.playerGroupId,
        needToRepeat: this.isRepeatRequired,
        repetitionPeriods: this.repeatPeriods,
      };
    },

    confirmRecordCreation(onConfirmed) {
      confirmAction(
          this,
          'Are you sure you want to schedule this river rumble {env}?',
          'Create',
          onConfirmed,
      );
    },

    createRecord(record) {
      const data = JSON.stringify(record);

      HTTP.post('/river_rumble/timetable/add', data, {headers: {'Content-Type': 'application/json'}})
          .then(response => {
            const result = response.data.result;

            const RESULT_SUCCESS = 1;
            if (result.code === RESULT_SUCCESS) {
              notifier.showSuccessAfterTransition('River rumble(s) scheduled.');
              this.$router.push({name: 'river_rumble_timetable'});
              return;
            }

            notifier.showError(result.message, {sender: this});
          })
          .catch(e => {
            notifier.showError(e, {sender: this});
          });
    },

    initSeedRandomizer() {
      const randSeed = new Fnv32a();
      randSeed.writeUint32(this.initStamp);

      this.seedRandomizer = new Random(randSeed.value);
    },

    randomizeSeed() {
      this.seed = this.seedRandomizer.next() % 10000
    }
  },

  filters: {
    getMomentStr,
    convertVersionCodeToStr,
  },
};
</script>

<style scoped>
.seed-container {
  gap: 1em;
}
</style>
